import { useState, useEffect, useCallback } from 'react';
import {
  initializeGoogleIdentity,
  renderGoogleSignInButton,
  renderGoogleOneTap,
  cancelGoogleOneTap,
  waitForGoogleIdentity,
  decodeGoogleToken,
  isGoogleIdentityLoaded
} from '../utils/googleIdentity';

export const useGoogleIdentity = (clientId) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  // Wait for Google Identity Services to load
  useEffect(() => {
    const loadGoogleIdentity = async () => {
      await waitForGoogleIdentity();
      setIsLoaded(true);
    };

    loadGoogleIdentity();
  }, []);

  // Initialize Google Identity Services
  const initialize = useCallback((callback) => {
    if (!isLoaded || !clientId) {
      setError('Google Identity Services not loaded or client ID not provided');
      return false;
    }

    try {
      initializeGoogleIdentity(clientId, callback);
      setIsInitialized(true);
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to initialize Google Identity Services');
      return false;
    }
  }, [isLoaded, clientId]);

  // Handle Google Sign-In
  const handleGoogleSignIn = useCallback((credential) => {
    try {
      const userData = decodeGoogleToken(credential);
      if (userData) {
        setUser(userData);
        setError(null);
        return userData;
      } else {
        setError('Failed to decode Google token');
        return null;
      }
    } catch (err) {
      setError('Error processing Google sign-in');
      return null;
    }
  }, []);

  // Render Google Sign-In button
  const renderSignInButton = useCallback((elementId, options = {}) => {
    if (!isLoaded) {
      setError('Google Identity Services not loaded yet');
      return false;
    }

    try {
      renderGoogleSignInButton(elementId, options);
      return true;
    } catch (err) {
      setError('Failed to render Google Sign-In button');
      return false;
    }
  }, [isLoaded]);

  // Render Google One Tap
  const renderOneTap = useCallback((options = {}) => {
    if (!isLoaded) {
      setError('Google Identity Services not loaded yet');
      return false;
    }

    try {
      renderGoogleOneTap(options);
      return true;
    } catch (err) {
      setError('Failed to render Google One Tap');
      return false;
    }
  }, [isLoaded]);

  // Cancel Google One Tap
  const cancelOneTap = useCallback(() => {
    if (isLoaded) {
      cancelGoogleOneTap();
    }
  }, [isLoaded]);

  // Sign out
  const signOut = useCallback(() => {
    setUser(null);
    setError(null);
  }, []);

  // Check if user is signed in
  const isSignedIn = useCallback(() => {
    return user !== null;
  }, [user]);

  return {
    // State
    isLoaded,
    isInitialized,
    user,
    error,
    
    // Methods
    initialize,
    handleGoogleSignIn,
    renderSignInButton,
    renderOneTap,
    cancelOneTap,
    signOut,
    isSignedIn,
    
    // Utility
    isGoogleIdentityLoaded: () => isGoogleIdentityLoaded()
  };
}; 